/**
 * THE HOME EDIT SHEET, which no artboard draws, so it is built from drawn parts and marked for the
 * owner to look at: "Change something" on Home's "What we have written down"
 * (`faithfully-kept-business/design/home/Main.dc.html`) opens Schedule's sheet (`Sheet.tsx`), with
 * Feedback's text field for each of Home's own rows, Leave alone, Pets and Getting in, Home's codes
 * line under them, then Schedule's "Send the request" and the first sentence of the line under it.
 * The words are in `words.ts`.
 *
 * - EACH ROW AS THE OFFICE WROTE IT DOWN, to change in place; a row ops did not send is an empty
 *   field to write in. "The house" is not here: the seam's `home.edit` carries the three rows and no
 *   rooms (ops `docs/seams/home.md`, section 4).
 * - WHAT IS SENT is a `home.edit` naming the home, with each row she changed as the proposed text,
 *   exactly as she typed it. A row left as it was, typed back as it was, or emptied proposes
 *   nothing, because the seam's edit carries text and has no word for taking a row away; with
 *   nothing proposed there is nothing to send. What she typed travels in the body of the request,
 *   never in a path or a query.
 * - ASKING IS ALL IT DOES (CLAUDE.md binding rule 1). A request the door has written closes the
 *   sheet and is shown on the home as sent to the office; what is written down changes only when
 *   ops pushes the home changed, and where the edit stands is Home's to draw from the next read.
 * - ONE REQUEST HOWEVER OFTEN IT IS TAPPED, AND HOWEVER OFTEN THE SHEET IS CLOSED. A key is minted
 *   once for each answer, so a retry after a failure names the same request and the door writes
 *   one row, and a changed answer is a new key. While one is out, another tap does nothing. Both
 *   are held by Home for the home (`lib/asks.ts`), never by the sheet, so a sheet closed while an
 *   edit is out loses nothing, and one opened again finds it still out or reuses its key.
 * - A CODE STAYS OUT. The door runs the words test before it stores anything, and a row that looks
 *   like it holds a code is refused there, by name: the sheet says the seam's words, marks that
 *   field and gives it the focus, and keeps what she wrote so she can take the code out. Getting in
 *   is words about how to arrive, never what opens anything.
 * - WHAT THE DOOR ANSWERS decides what follows, whether or not the sheet is still open: sent is
 *   Home's to show; a 401 is the way to the door; an edit already waiting reads Home again and
 *   closes the sheet; a 404, the home gone or not hers to ask about now, which the door answers
 *   alike, says the home can no longer be changed here, with the phone, in the same words either
 *   way, takes the focus and reads Home again, and the sheet stays open on it even where Home read
 *   again no longer holds the home (Home keeps it drawn), never closing in silence (the owner,
 *   2026-10-20); anything else says it did not go through, with the phone, and leaves the sheet as
 *   it was to try again. What the sheet says, it says only while it is open.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { sendRequest, type HomeWrittenDown, type TooMany } from '../api.ts';
import { keyFor, type Asking } from '../lib/asks.ts';
import {
  EDIT_HOME,
  FAILED_BEFORE_PHONE,
  HOME,
  LOOKS_LIKE_A_CODE,
  SCHEDULE,
  tooManyBeforePhone,
} from '../words.ts';
import { NAVY_BUTTON } from './buttons.ts';
import { Phone } from './Phone.tsx';
import { Sheet } from './Sheet.tsx';

/**
 * The request door's limit on each row of an edit: provisional P7, which lives in
 * `api/src/seam/requests.ts`, and which `api/test/requests.test.ts` holds this to.
 */
export const HOME_EDIT_LINE_MAX = 500;

/** A row she may ask to change, by the seam's name for it. */
type Row = 'do_not_list' | 'pets' | 'entry_notes';

/** The rows, in the seam's order, each with Home's label for it and what the office wrote. */
const ROWS: readonly {
  readonly row: Row;
  readonly label: string;
  readonly written: (home: HomeWrittenDown) => string | undefined;
}[] = [
  { row: 'do_not_list', label: HOME.leaveAlone, written: (home) => home.doNotList },
  { row: 'pets', label: HOME.pets, written: (home) => home.pets },
  { row: 'entry_notes', label: HOME.gettingIn, written: (home) => home.entryNotes },
];

const isRow = (name: string): name is Row => ROWS.some(({ row }) => row === name);

/**
 * What the sheet says after an ask that was not sent: a code, in the row named, past the door's own
 * limits, the home no longer one she may ask about here (the door's 404), or a failure.
 */
type Problem = { readonly code: Row | null } | 'too_many' | 'not_here' | 'failed';

const QUESTION = 'text-body font-semibold text-text-body';

/** Feedback's field. */
const FIELD =
  'mt-2 field-sizing-content max-h-48 min-h-tap-call resize-none rounded-control border-2 ' +
  'border-border-strong bg-surface-raised px-4 py-3 text-body text-text-body';

interface EditHomeProps {
  readonly home: HomeWrittenDown;
  /** Whether Home names each home, as it does when there are several, so the sheet says which. */
  readonly named: boolean;
  /** The household Home shows, which the request is about. */
  readonly household: string;
  /** Whether an edit of the home is out, and its keys, held by Home for the home. */
  readonly asking: Asking;
  /** Closes the sheet, having asked nothing more. */
  readonly onClose: () => void;
  /** The door wrote the request, whose id this is. */
  readonly onSent: (id: string) => void;
  /** The session has ended. */
  readonly onSignedOut: () => void;
  /** What Home shows may be out of date: read it again. */
  readonly onStale: () => void;
}

export function EditHome({
  home,
  named,
  household,
  asking,
  onClose,
  onSent,
  onSignedOut,
  onStale,
}: EditHomeProps) {
  const leaveAloneId = useId();
  const petsId = useId();
  const gettingInId = useId();
  const ids: Record<Row, string> = {
    do_not_list: leaveAloneId,
    pets: petsId,
    entry_notes: gettingInId,
  };
  const problemId = useId();
  const [typed, setTyped] = useState<Record<Row, string>>(() => ({
    do_not_list: home.doNotList ?? '',
    pets: home.pets ?? '',
    entry_notes: home.entryNotes ?? '',
  }));
  const [problem, setProblem] = useState<Problem | null>(null);
  /** Whose count of the door's own limits it was past, and when to try again, as the door said. */
  const [limit, setLimit] = useState<TooMany>();
  const fields = useRef(new Map<Row, HTMLTextAreaElement>());
  /** What the sheet says of the door's 404, which takes the focus. */
  const notHereLine = useRef<HTMLParagraphElement>(null);
  /** Whether this sheet is still open, and so has anything to say. */
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The home is no longer one she may ask about here: what the sheet says of it takes the focus, so
  // it is read out and stays in the sheet, where Escape still closes it.
  useEffect(() => {
    if (problem === 'not_here') notHereLine.current?.focus();
  }, [problem]);

  /** Each row she changed, as she typed it: never blank, and never what the office wrote. */
  const proposed = ROWS.flatMap(({ row, written }) => {
    const text = typed[row];
    const was = (written(home) ?? '').trim();
    return text.trim() === '' || text.trim() === was ? [] : [[row, text] as const];
  });

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (asking.out || proposed.length === 0) return;
    asking.out = true;
    setProblem(null);
    const payload = { home_id: home.id, ...Object.fromEntries(proposed) };
    const outcome = await sendRequest({
      key: keyFor(asking, JSON.stringify(payload)),
      type: 'home.edit',
      household,
      payload,
    });
    asking.out = false;
    switch (outcome.outcome) {
      case 'sent':
        asking.keys.clear();
        onSent(outcome.id);
        return;
      case 'signed_out':
        onSignedOut();
        return;
      // An edit of the home already waits on the office: asking again would be refused again, so
      // Home is read again, and says where that one stands.
      case 'already_asked':
        onStale();
        if (mounted.current) onClose();
        return;
      // The home is gone, or not hers to ask about now, which the door answers alike (binding rule
      // 2): said in the sheet, in the same words either way, never silent, and Home read again.
      case 'not_found':
        if (mounted.current) setProblem('not_here');
        onStale();
        return;
      case 'looks_like_a_code': {
        if (!mounted.current) return;
        const row = isRow(outcome.field) ? outcome.field : null;
        setProblem({ code: row });
        if (row !== null) fields.current.get(row)?.focus();
        return;
      }
      case 'too_many':
        if (!mounted.current) return;
        setLimit(outcome.limit);
        setProblem('too_many');
        return;
      default:
        if (mounted.current) setProblem('failed');
    }
  }

  const invalid = (row: Row) => typeof problem === 'object' && problem?.code === row;

  return (
    <Sheet
      heading={EDIT_HOME.change}
      sub={
        named && home.label !== undefined ? (
          <p className="text-small text-text-muted">{home.label}</p>
        ) : undefined
      }
      onClose={onClose}
    >
      <form method="post" onSubmit={(event) => void send(event)} className="mt-4 flex flex-col">
        {ROWS.map(({ row, label }, index) => (
          <div key={row} className={`flex flex-col ${index === 0 ? '' : 'mt-5'}`}>
            <label htmlFor={ids[row]} className={QUESTION}>
              {label}
            </label>
            <textarea
              ref={(field) => {
                if (field) fields.current.set(row, field);
                else fields.current.delete(row);
              }}
              id={ids[row]}
              value={typed[row]}
              onChange={(event) => {
                const text = event.target.value;
                setTyped((held) => ({ ...held, [row]: text }));
              }}
              rows={1}
              maxLength={HOME_EDIT_LINE_MAX}
              aria-invalid={invalid(row) ? true : undefined}
              aria-describedby={invalid(row) ? problemId : undefined}
              className={FIELD}
            />
          </div>
        ))}

        <p className="mt-4 text-small text-pretty text-text-muted">{HOME.codes}</p>

        <div aria-live="polite" id={problemId}>
          {problem === null ? null : (
            <p
              ref={problem === 'not_here' ? notHereLine : undefined}
              tabIndex={problem === 'not_here' ? -1 : undefined}
              className="mt-4 text-body text-pretty"
            >
              {problem === 'failed' || problem === 'too_many' || problem === 'not_here' ? (
                <>
                  {problem === 'too_many'
                    ? tooManyBeforePhone(limit)
                    : problem === 'not_here'
                      ? EDIT_HOME.notHereBeforePhone
                      : FAILED_BEFORE_PHONE}{' '}
                  <Phone after="." />
                </>
              ) : (
                LOOKS_LIKE_A_CODE
              )}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={proposed.length === 0}
          className={`mt-6 ${NAVY_BUTTON} disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {SCHEDULE.send}
        </button>
      </form>
      <p className="mt-3 text-small text-pretty text-text-muted">{EDIT_HOME.nothingChanges}</p>
    </Sheet>
  );
}
